'use client';
import { AlbumLoader } from '@/components/ui/album-loader';
import * as React from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { ArchiveRestore, BookImage, Plus, Search, SearchX } from 'lucide-react';
import { Link } from '@/i18n/navigation';
import { api, ApiError } from '@/lib/api';
import type { AlbumSummary } from '@freealbum/shared';
import { Card, EmptyState, Input, Select, toast } from '@freealbum/ui';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { LinkIcon } from '@/components/ui/link-status';
import { ExportDialog } from '@/components/export/export-dialog';
import { useApiErrorMessage } from '@/hooks/use-api-error';
import { formatDate } from '@/lib/utils';
import { AlbumCard, type AlbumCardProps } from './album-card';
import { RestoreDialog } from './restore-dialog';

type SortKey = 'recent' | 'title' | 'photos';
const SORTS: SortKey[] = ['recent', 'title', 'photos'];

/**
 * How many cards the grid shows before asking. Libraries are capped in the hundreds, so the whole
 * list is already in memory — this only keeps the first screen short, and "show more" appends to it
 * instead of paging, so nothing a reader has already scrolled past disappears (UX-43).
 */
const PAGE_SIZE = 12;

/**
 * Fold a title down to what a reader would type: decomposed, without combining marks, lowercased.
 * Searching "האלבום" therefore finds the pointed "הָאַלְבּוֹם", which is how Hebrew titles are stored.
 */
function fold(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();
}

export function AlbumsGrid() {
  const t = useTranslations('albums');
  const tb = useTranslations('backup');
  const tc = useTranslations('common');
  const locale = useLocale();
  const errorMessage = useApiErrorMessage();

  // `t` and the error formatter are read through a ref so that `load` — and the effect that calls
  // it — keep one identity for the life of the component. See docs/10-ux-audit.md · UX-06.
  const format = React.useRef<(err: unknown) => string>(() => '');
  format.current = (err: unknown) => (err instanceof ApiError ? errorMessage(err) : t('loadFailed'));
  /** Only the newest request may write state; older ones have been superseded. */
  const requestId = React.useRef(0);

  const [albums, setAlbums] = React.useState<AlbumSummary[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState<{ id: string; action: AlbumCardProps['busy'] } | null>(null);
  const [deleting, setDeleting] = React.useState<AlbumSummary | null>(null);
  const [exporting, setExporting] = React.useState<AlbumSummary | null>(null);
  const [restoring, setRestoring] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [sort, setSort] = React.useState<SortKey>('recent');
  const [visible, setVisible] = React.useState(PAGE_SIZE);

  const load = React.useCallback(async (signal?: AbortSignal) => {
    const id = ++requestId.current;
    try {
      const { albums: list } = await api.albums.list(signal);
      if (id !== requestId.current) return;
      setAlbums(list);
      setError(null);
    } catch (err) {
      // An aborted request is not a failure the reader should hear about.
      if (signal?.aborted || id !== requestId.current) return;
      setAlbums([]);
      setError(format.current(err));
    }
  }, []);

  React.useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // A narrowed or re-ordered list starts from the top again.
  React.useEffect(() => setVisible(PAGE_SIZE), [query, sort]);

  const collator = React.useMemo(() => new Intl.Collator(locale, { sensitivity: 'base', numeric: true }), [locale]);

  /** Filtering and sorting are local: the whole library is already loaded. */
  const matches = React.useMemo(() => {
    const needle = fold(query);
    const list = needle ? (albums ?? []).filter((a) => fold(a.title).includes(needle)) : [...(albums ?? [])];
    const edited = (a: AlbumSummary) => Date.parse(a.updatedAt);
    return list.sort((a, b) => {
      if (sort === 'title') {
        // An unnamed album has nothing to alphabetise, so it sits at the end rather than the top.
        if (!a.title !== !b.title) return a.title ? -1 : 1;
        return collator.compare(a.title, b.title) || edited(b) - edited(a);
      }
      if (sort === 'photos') return b.photoCount - a.photoCount || edited(b) - edited(a);
      return edited(b) - edited(a);
    });
  }, [albums, query, sort, collator]);

  const duplicate = async (album: AlbumSummary) => {
    setBusy({ id: album.id, action: 'duplicating' });
    try {
      await api.albums.duplicate(album.id);
      toast({ title: t('duplicated'), variant: 'success' });
      await load();
    } catch (err) {
      toast({ title: errorMessage(err), variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  /**
   * Failure is deliberately *not* caught here: the confirmation shows it inline and stays open with a
   * Retry, so a delete that did not happen cannot look like one that did (UX-30).
   */
  const confirmDelete = async () => {
    if (!deleting) return;
    const album = deleting;
    setBusy({ id: album.id, action: 'deleting' });
    try {
      await api.albums.delete(album.id);
      setAlbums((list) => (list ?? []).filter((a) => a.id !== album.id));
      toast({ title: t('deleted'), variant: 'success' });
    } finally {
      setBusy(null);
    }
  };

  const extend = async (album: AlbumSummary) => {
    setBusy({ id: album.id, action: 'extending' });
    try {
      const { purgeAt } = await api.albums.extendRetention(album.id);
      setAlbums((list) => (list ?? []).map((a) => (a.id === album.id ? { ...a, purgeAt } : a)));
      toast({ title: t('purge.extended', { date: formatDate(purgeAt, locale) }), variant: 'success' });
    } catch (err) {
      toast({ title: errorMessage(err), variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-ink">{t('title')}</h1>
          <p className="mt-1 text-sm text-ink-2">{t('lead')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setRestoring(true)}
            className="inline-flex h-touch items-center gap-1.5 rounded-md border border-line-strong bg-surface px-4 text-sm font-medium text-ink shadow-xs hover:bg-paper-2"
          >
            <ArchiveRestore aria-hidden className="size-4" />
            {tb('restore.action')}
          </button>
          <Link
            href="/albums/new"
            className="inline-flex h-touch items-center gap-1.5 rounded-md bg-accent px-4 text-sm font-medium text-accent-fg shadow-xs hover:bg-accent-hover"
          >
            <LinkIcon><Plus aria-hidden className="size-4" /></LinkIcon>
            {t('new')}
          </Link>
        </div>
      </div>

      {albums !== null && !error && albums.length > 0 ? (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t('search.label')}
            placeholder={t('search.placeholder')}
            startIcon={<Search aria-hidden />}
            className="k-nikud"
            containerClassName="min-w-56 flex-1"
          />
          <Select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            aria-label={t('sort.label')}
            options={SORTS.map((value) => ({ value, label: t(`sort.${value}`) }))}
            className="w-auto"
          />
        </div>
      ) : null}

      {albums === null ? (
        <div className="grid place-items-center py-20">
          <AlbumLoader label={t('loading')} />
        </div>
      ) : error ? (
        <Card className="p-6 text-center text-sm text-danger">{error}</Card>
      ) : albums.length === 0 ? (
        <EmptyState
          icon={<BookImage className="size-10" />}
          title={t('empty.title')}
          description={t('empty.body')}
          action={
            <>
              <Link
                href="/albums/new"
                className="inline-flex h-touch items-center gap-1.5 rounded-md bg-accent px-4 text-sm font-medium text-accent-fg shadow-xs hover:bg-accent-hover"
              >
                <LinkIcon><Plus aria-hidden className="size-4" /></LinkIcon>
                {t('empty.cta')}
              </Link>
              {/* A first-time visitor may be arriving with a backup from another device. */}
              <button
                type="button"
                onClick={() => setRestoring(true)}
                className="inline-flex h-touch items-center gap-1.5 rounded-md border border-line-strong bg-surface px-4 text-sm font-medium text-ink shadow-xs hover:bg-paper-2"
              >
                <ArchiveRestore aria-hidden className="size-4" />
                {tb('restore.action')}
              </button>
            </>
          }
        />
      ) : matches.length === 0 ? (
        /* A search that found nothing is a dead end unless it offers the way back out. */
        <EmptyState
          icon={<SearchX className="size-10" />}
          title={t('search.noResults.title')}
          description={t('search.noResults.body', { query })}
          action={
            <button
              type="button"
              onClick={() => setQuery('')}
              className="inline-flex h-touch items-center gap-1.5 rounded-md border border-line-strong bg-surface px-4 text-sm font-medium text-ink shadow-xs hover:bg-paper-2"
            >
              {t('search.clear')}
            </button>
          }
        />
      ) : (
        <>
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {matches.slice(0, visible).map((album) => (
              <li key={album.id}>
                <AlbumCard
                  album={album}
                  busy={busy?.id === album.id ? busy.action : undefined}
                  onDuplicate={() => duplicate(album)}
                  onDelete={() => setDeleting(album)}
                  onExport={() => setExporting(album)}
                  onExtendRetention={() => extend(album)}
                />
              </li>
            ))}
          </ul>
          {matches.length > visible ? (
            <div className="mt-6 flex flex-col items-center gap-2">
              <p aria-live="polite" className="text-xs text-ink-3">
                {t('results.showing', { shown: visible, total: matches.length })}
              </p>
              <button
                type="button"
                onClick={() => setVisible((n) => n + PAGE_SIZE)}
                className="inline-flex h-touch items-center gap-1.5 rounded-md border border-line-strong bg-surface px-4 text-sm font-medium text-ink shadow-xs hover:bg-paper-2"
              >
                {t('results.more')}
              </button>
            </div>
          ) : null}
        </>
      )}

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        onConfirm={confirmDelete}
        title={t('deleteConfirm.title')}
        description={deleting ? t('deleteConfirm.body', { title: deleting.title || t('card.untitled') }) : undefined}
        confirmLabel={t('deleteConfirm.confirm')}
        cancelLabel={tc('cancel')}
      />

      <RestoreDialog open={restoring} onOpenChange={setRestoring} />

      {exporting ? (
        <ExportDialog
          albumId={exporting.id}
          albumTitle={exporting.title}
          pageCount={exporting.innerPages}
          page={exporting.page}
          open
          onOpenChange={(open) => !open && setExporting(null)}
        />
      ) : null}
    </>
  );
}
